import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Target, Users, ChevronRight, UserPlus, Search,
  ArrowLeft, Copy, Check, BookOpen, BarChart3, Settings
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = window.location.origin;

export default function CoachStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addingByEmail, setAddingByEmail] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only
  useEffect(() => { fetchStudents(); }, []);

  const fetchStudents = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/students`);
      if (res.ok) {
        const data = await res.json();
        setStudents(data.students || []);
      } else if (res.status === 403) {
        navigate("/dashboard");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const generateInvite = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/students/invite`, {
      });
      if (res.ok) {
        const data = await res.json();
        setInviteLink(data.invite_url);
        setShowInvite(true);
      }
    } catch (e) {
      toast.error("Failed to generate invite");
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const addByEmail = async () => {
    if (!addEmail.trim()) return;
    setAddingByEmail(true);
    try {
      const res = await authFetch(`${API}/api/coach/students/add-by-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: addEmail.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "connected") {
          toast.success(`${data.player?.name || addEmail} added!`);
          fetchStudents();
        } else if (data.status === "already_connected") {
          toast.info("Already connected to this player");
        } else {
          toast.success("Invite saved — they'll be linked when they sign up");
        }
        setAddEmail("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add");
      }
    } catch (e) { toast.error("Failed to add player"); }
    finally { setAddingByEmail(false); }
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <div>
              <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">Coach</Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigate("/coach/settings")} className="text-[#555] hover:text-[#111]">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Nav Tabs */}
      <nav className="px-6 py-3 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Target className="w-4 h-4" />, path: "/coach" },
            { id: "students", label: "Students", icon: <Users className="w-4 h-4" />, path: "/coach/students" },
            { id: "library", label: "Drill Library", icon: <BookOpen className="w-4 h-4" />, path: "/coach/library" },
            { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, path: "/coach/analytics" }
          ].map((tab) => (
            <Button key={tab.id} variant="ghost" onClick={() => navigate(tab.path)}
              className={`flex items-center gap-2 ${tab.id === "students" ? "bg-gray-200 text-[#111]" : "text-[#555] hover:text-[#111]"}`}>
              {tab.icon} {tab.label}
            </Button>
          ))}
        </div>
      </nav>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Title + Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#111]">Students</h1>
              <p className="text-[#555] text-sm">{students.length} student{students.length !== 1 ? "s" : ""} in your squad</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={generateInvite} variant="outline" className="text-[#555]" data-testid="invite-student-btn">
                <Copy className="w-4 h-4 mr-2" /> Share Link
              </Button>
            </div>
          </div>

          {/* Add by Email */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <p className="text-[#111] font-medium text-sm mb-2">Add player by email</p>
              <div className="flex gap-2">
                <Input
                  type="email" value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addByEmail()}
                  placeholder="player@email.com"
                  className="bg-gray-100 border-gray-200 text-[#111] placeholder:text-[#555]/50"
                  data-testid="add-email-input"
                />
                <Button onClick={addByEmail} disabled={addingByEmail || !addEmail.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0" data-testid="add-email-btn">
                  {addingByEmail ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Add</>}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Invite Link Banner */}
          {showInvite && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-emerald-500/10 border-emerald-500/30">
                <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-700 mb-1">Share this invite link with your student</p>
                    <p className="text-xs text-[#555] font-mono break-all" data-testid="invite-link-text">{inviteLink}</p>
                  </div>
                  <Button onClick={copyInvite} variant="outline" size="sm" className="border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/10 shrink-0" data-testid="copy-invite-btn">
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Search */}
          {students.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
              <Input
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white border-gray-200 text-[#111] placeholder:text-[#555]"
                data-testid="search-students-input"
              />
            </div>
          )}

          {/* Student List */}
          {filtered.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-emerald-700" />
                </div>
                <h3 className="text-xl font-semibold text-[#111] mb-2">
                  {students.length === 0 ? "No Students Yet" : "No results found"}
                </h3>
                <p className="text-[#555] mb-6">
                  {students.length === 0
                    ? "Generate an invite link and share it with your students to get started."
                    : "Try a different search term."}
                </p>
                {students.length === 0 && (
                  <Button onClick={generateInvite} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <UserPlus className="w-4 h-4 mr-2" /> Generate Invite Link
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((student, i) => (
                <motion.div key={student.student_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card
                    className="bg-white border-gray-200 hover:border-emerald-500/30 cursor-pointer transition-all"
                    onClick={() => navigate(`/coach/students/${student.student_id}`)}
                    data-testid={`student-card-${student.student_id}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-700 font-bold text-lg">
                          {student.name?.charAt(0) || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-[#111]">{student.name}</p>
                          <p className="text-sm text-[#555]">
                            {student.handicap != null ? `HCP ${student.handicap}` : "No handicap"} &middot;{" "}
                            {student.sessions_this_week}/{student.expected_sessions} sessions this week
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                          <p className={`font-bold text-lg ${
                            student.compliance >= 75 ? "text-emerald-700" :
                            student.compliance >= 50 ? "text-[#111]" : "text-red-400"
                          }`}>{student.compliance}%</p>
                          <p className="text-xs text-[#555]">compliance</p>
                        </div>
                        <Badge className={`sm:hidden ${
                          student.compliance >= 75 ? "bg-emerald-500/20 text-emerald-700" :
                          student.compliance >= 50 ? "bg-amber-500/20 text-[#111]" : "bg-red-500/20 text-red-400"
                        }`}>{student.compliance}%</Badge>
                        <ChevronRight className="w-5 h-5 text-[#555]" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
